/**
 * MenuSearchModal 组件的类型定义
 */

import { CryptoCardItem, NewsItem, StockCardItem } from "./SearchResult/types";

import { SearchOpenData } from "@features/api/openData";

/**
 * 视图状态枚举
 */
export enum ViewState {
  /** 推荐区域 */
  RECOMMENDED = "recommended",
  /** 搜索结果概览 */
  SEARCH_OVERVIEW = "overview",
  /** 分类详细页面 */
  CATEGORY_DETAIL = "detail",
}

/**
 * 分类类型枚举
 */
export enum CategoryType {
  /** 股票主题分类 */
  STOCK_THEMES = "stock_themes",
  /** 加密货币主题分类 */
  CRYPTO_THEMES = "crypto_themes",
  /** 股票分类 */
  STOCKS = "stocks",
  /** 加密货币分类 */
  CRYPTO = "crypto",
  /** 新闻分类 */
  NEWS = "news",
}

/**
 * 推荐区域 Tab 类型
 */
export type RecommendedTabType = "stock" | "crypto";

/**
 * 搜索状态接口
 */
export interface SearchState {
  /** 当前视图状态 */
  viewState: ViewState;
  /** 搜索关键词 */
  keywords: string;
  /** 选中的分类 */
  selectedCategory?: CategoryType;
  /** 分类标题 */
  categoryTitle?: string;
  /** 推荐区域当前选中的 Tab */
  recommendedTab: RecommendedTabType;
}

/**
 * 股票主题项接口
 */
interface StockThemeItem {
  /** 显示名称 */
  value: string;
  /** 标签 */
  label: string;
  /** API键名 */
  apiKey?: string;
  /** 描述 */
  desc?: string;
  /** SEO文本 */
  seoText?: string;
  /** 图标 */
  icon?: string;
}

/**
 * 加密货币主题项接口
 */
interface CryptoThemeItem {
  /** 显示名称 */
  value: string;
  /** 标签 */
  label: string;
  /** API键名 */
  apiKey?: string;
  /** 描述 */
  desc?: string;
  /** SEO文本 */
  seoText?: string;
  /** 图标 */
  icon?: string;
}

/**
 * 股票数据项接口（来自API）
 */
interface StockDataItem {
  /** 类型 */
  type: "STOCK";
  /** ID */
  id: string;
  /** 名称 */
  name: string;
  /** 描述 */
  desc: string;
  /** 图片URL */
  image?: string;
  /** 股票代码 */
  symbol: string;
  /** 主题标签 */
  themes?: string[];
}

/**
 * 加密货币数据项接口（来自API）
 */
interface CryptoDataItem {
  /** 类型 */
  type: "TOKEN";
  /** ID */
  id: string;
  /** 名称 */
  name: string;
  /** 图片URL */
  image?: string;
  /** 代币符号 */
  symbol: string;
}

/**
 * 新闻数据项接口（来自API）
 */
interface NewsDataItem {
  /** 类型 */
  type: "CONTENT";
  /** ID */
  id: string;
  /** 标题 */
  name: string;
  /** 描述内容 */
  desc: string;
  /** 发布时间 */
  issue_date: number;
  /** 来源链接 */
  source_link: string;
  /** 分类 */
  categories: string[];
}

/**
 * 处理后的搜索数据结构
 */
export interface ProcessedSearchData {
  /** 前端筛选的股票主题 */
  stockThemes: SearchOpenData[];
  /** 前端筛选的加密货币主题 */
  cryptoThemes: SearchOpenData[];
  /** API返回的股票数据 */
  stocks: StockDataItem[];
  /** API返回的加密货币数据 */
  crypto: CryptoDataItem[];
  /** API返回的新闻数据 */
  news: NewsDataItem[];
}

// 保持向后兼容的类型别名
export interface ThemeItem {
  value: string;
  label: string;
  /** @deprecated */
  apiKey?: string;
  desc?: string;
  seoText?: string;
  icon?: string;
  summary?: string;
}

/**
 * 分类显示信息接口
 */
export interface CategoryInfo {
  /** 分类类型 */
  type: CategoryType;
  /** 分类标题 */
  title: string;
  /** 显示数据 */
  data:
    | StockThemeItem[]
    | CryptoThemeItem[]
    | StockDataItem[]
    | CryptoDataItem[]
    | NewsDataItem[]
    | ThemeItem[]
    | NewsItem[]
    | SearchOpenData[];
  /** 总数 */
  total: number;
  /** 是否显示 View All 按钮 */
  showViewAll: boolean;
}

/**
 * 分类数据限制常量
 */
export const CATEGORY_LIMITS = {
  [CategoryType.STOCK_THEMES]: 10,
  [CategoryType.CRYPTO_THEMES]: 10,
  [CategoryType.STOCKS]: 4,
  [CategoryType.CRYPTO]: 4,
  [CategoryType.NEWS]: 4,
} as const;

/**
 * 分类标题映射
 */
export const CATEGORY_TITLES = {
  [CategoryType.STOCK_THEMES]: "Stock Themes",
  [CategoryType.CRYPTO_THEMES]: "Crypto Themes",
  [CategoryType.STOCKS]: "Stocks",
  [CategoryType.CRYPTO]: "Crypto",
  [CategoryType.NEWS]: "News",
} as const;

/**
 * MenuSearchModal 组件属性接口
 */
export interface MenuSearchModalProps {
  /** 类型 */
  type?: "normal" | "portfolio";
  /** 推荐区域默认 tab */
  defaultRecommendedTab?: "stock" | "crypto";
  /** 关闭回调 */
  onClose: () => void;
  /** 项目点击回调 */
  onItemClick?: (item: { keyword: string; token: string }) => void;
  onCompleted?: () => void;
}

/**
 * SearchOverview 组件属性接口
 */
export interface SearchOverviewProps {
  /** 是否显示加载状态 */
  isLoading: boolean;
  /** 关闭回调 */
  onClose: () => void;
  /** 分类信息列表 */
  categories: CategoryInfo[];
  /** View All 按钮点击回调 */
  onViewAll: (category: CategoryType, title: string) => void;
  /** 来源类型：normal | portfolio（影响左侧交互） */
  originType?: "normal" | "portfolio";
}

/**
 * CategoryDetail 组件属性接口
 */
export interface CategoryDetailProps {
  /** 关闭回调 */
  onClose: () => void;
  /** 分类类型 */
  categoryType: CategoryType;
  originType?: "normal" | "portfolio";
  /** 分类标题 */
  title: string;
  /** 分类数据 */
  data:
    | StockThemeItem[]
    | CryptoThemeItem[]
    | StockDataItem[]
    | CryptoDataItem[]
    | NewsDataItem[]
    | ThemeItem[]
    | StockCardItem[]
    | CryptoCardItem[]
    | NewsItem[]
    | SearchOpenData[];
}

/**
 * RecommendedArea 组件属性接口
 */
export interface RecommendedAreaProps {
  /** 类型 */
  type?: "normal" | "portfolio";
  /** 关闭回调 */
  onClose: () => void;
  /** 当前激活的 Tab */
  activeTab: RecommendedTabType;
  /** Tab 切换回调 */
  onTabChange: (tab: RecommendedTabType) => void;
}
